<script setup lang="ts">
type FabSize = 'medium' | 'large'
type FabState = 'default' | 'hover' | 'active' | 'focused' | 'disabled'

const props = withDefaults(
    defineProps<{
      text?: string
      icon?: string
      size?: FabSize
      state?: FabState
      disabled?: boolean
      type?: 'button' | 'submit' | 'reset'
    }>(),
    {
      text: '',
      icon: undefined,
      size: 'medium',
      state: 'default',
      disabled: false,
      type: 'button',
    },
)

const isDisabled = computed(() => props.disabled || props.state === 'disabled')
</script>

<template>
  <button
      class="fab"
      :class="[size, state, { disabled: isDisabled }]"
      :disabled="isDisabled"
      :type="type"
  >
    <span v-if="icon" class="material-symbols-rounded">{{ icon }}</span>
    <span v-if="text">{{ text }}</span>
  </button>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/index" as *;
@use "~/assets/scss/app" as *;
.fab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
  gap: 8px;
  user-select: none;

  &.medium {
    padding: 8px 16px;
    font-size: 14px;
    border-radius: 20px;
  }

  &.large {
    padding: 12px 16px;
    font-size: 16px;
    border-radius: 52px;
  }

  &.default {
    background: var(--color-primary);
    color: var(--color-text-on-primary);
  }

  &:hover:not(.disabled) {
    background: var(--color-primary-hover);
  }

  &:active:not(.disabled) {
    background: var(--color-primary-active);
  }

  &:focus-visible:not(.disabled) {
    background: var(--color-primary-hover);
    outline: none;
    border: 2px solid var(--color-bg-elevated);
    box-shadow: 0 0 0 4px var(--color-primary-hover);
  }

  &.disabled {
    background: c.$enedis-neutral-40;
    color: var(--color-fab-muted);
    cursor: not-allowed;
  }

  .material-symbols-rounded {
    font-size: 24px;
    line-height: 1;
  }
}
</style>
